import type { ReactNode} from 'react';
import { useMemo } from 'react';

import { CardHeader } from '@ui/uikit/components/common/cards/CardHeader/internals/CardHeader';
import { PriceDetails } from '@ui/uikit/components/common/PriceDetails';
import { Leave } from '@ui/uikit/components/icons/mono/Leave';
import { Link } from '@utilities/Link';

import { cn, formatAmount } from '../../../../../utils';
import { ActivityStatus } from '../../../ActivityStatus';
import { Card } from '../../../Card';
import styles from './FarmCard.module.scss';

type StatusType = 'comingSoon' | 'ended' | 'started';

const getStatus = (isEnded: boolean, isComingSoon: boolean): StatusType => {
  if (isEnded) {
    return 'ended';
  }

  if (isComingSoon) {
    return 'comingSoon';
  }

  return 'started';
};

interface FarmCardProps {
  pool: {
    startDate: string | null;
    endDate: string | null;
    id: string | null;
    staked: string;
    participants: number | null;
    startApr: number | null;
    startReward: number | null;
    symbol: string | null;
    logoUrl: string | null;
  };
  apr: string;
  link: string;
  sharingControl?: ReactNode;
}

const FarmCard = ({ pool, sharingControl, link, apr }: FarmCardProps) => {
  const { startDate, endDate, staked, participants, startReward, logoUrl, symbol } = pool;

  const isEnded = !!endDate && new Date() > new Date(endDate);
  const isComingSoon = !!startDate && new Date(startDate) > new Date();
  const status = getStatus(isEnded, isComingSoon);

  const isViewButtonSecondary = status === 'comingSoon' || status === 'ended';

  const items = useMemo(
    () => [
      {
        description: 'Current APR',
        value: apr,
        tooltip: 'Total rewards available in this Farm.',
      },
      {
        description: 'Staked',
        value: `${staked} ${symbol}`,
        tooltip: 'Total tokens currently staked by participants in this Farm.',
      },
      {
        description: isComingSoon ? 'Registered' : 'Participants',
        value: formatAmount(participants || 0),
        tooltip: `Total number of ${isComingSoon ? 'registered' : 'participants'} in this Farm.`,
      },
    ],
    [apr, staked, symbol, isComingSoon, participants],
  );

  return (
    <Card className={styles['farm-card']} radius="large" padding="none">
      <CardHeader
        rewardTokenIconSrc={logoUrl || ''}
        slotIcon={<Leave color="var(--text-color-green)" />}
        reward={`${formatAmount(startReward || 0)} ${symbol}`}
      />
      <div className={styles['farm-card__content']}>
        <div className={styles['farm-card__body']}>
          <ActivityStatus status={status} endDate={endDate ? new Date(endDate) : null} />
          <PriceDetails items={items} isLoading={false} />
        </div>
        <div className={styles['farm-card__footer']}>
          <Link
            href={link}
            className={cn({
              [styles['primary']]: !isViewButtonSecondary,
              [styles['secondary']]: isViewButtonSecondary,
            })}
          >
            View Pool
          </Link>
          {sharingControl}
        </div>
      </div>
    </Card>
  );
};

export { FarmCard, type FarmCardProps };
